import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NGXLogger } from "ngx-logger";
import { Observable, Subscription } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { BaseService } from "../services/base.service";
import { environment } from "../../environments/environment";
import { SubscriptionPagedList, SubscriptionCreate, AuthSupportPagedList, TransactionPagedList } from "../../swagger-gen/web-secured/models";

@Injectable({
  providedIn: "root",
})
export class SubscriptionRepo extends BaseService {
  subscriptionUrl = environment.apiUrl + "/subscriptions";
  readonly BADGES_LIMIT = "20";
  readonly SUBSCRIPTIONS_LIMIT = '999999';

  constructor(private http: HttpClient, private logger: NGXLogger) {
    super();
  }

  /**
   * Get all subscriptions
   */
  getSubscriptionList(
    offset: number,
    limit: number | undefined,
    params: any[],
    search: string
  ): Observable<SubscriptionPagedList> {
    const url = this.subscriptionUrl + "/search";
    let requestString = this.pagination(offset, limit, this.SUBSCRIPTIONS_LIMIT);
    let queryParams = "";
    params.forEach((p: any) => {
      queryParams += "&" + p.key + "=" + p.value;
    });
    queryParams += '&search=' + search;
    requestString += queryParams;
    const requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      params: requestParams,
    };

    return this.http.post<SubscriptionPagedList>(url, {}, httpOptions).pipe(
      catchError(this.handleServerError)
    );
  }

  /**
   * Get subscription by uid
   * @param uid subscription uid
   */
  getSubscription(uid: string): Observable<Subscription> {
    const url = this.subscriptionUrl + `/${uid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return this.http.get<Subscription>(url, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get a subscription from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }


  /**
   * Unassign badge from subscription
   * @param uid subscription uid
   * @param body badge info
   */
  unassignBadgeFromSubscription(
    uid: string,
    body: any
  ): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/badges/remove`;
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return this.http.post<any>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Unassign badge response from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }


  exportUserDefinitionOfSubscribe(uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/users/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: "blob" as "json",
      observe: "response",
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  exportVehicleDefinitionOfSubscribe(uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/vehicles/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: "blob" as "json",
      observe: "response",
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  importUserDefinitionOfSubscrition(file: any, uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/users/import`;

    const httpOptions: any = {
      headers: new HttpHeaders().set(
        "Content-Type",
        "application/vnd.ms-excel"
      ),
      observe: "response",
    };

    return this.http
      .post<any>(url, file, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  importVehicleDefinitionOfSubscrition(
    file: any,
    uid: string
  ): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/vehicles/import`;

    const httpOptions: any = {
      headers: new HttpHeaders().set(
        "Content-Type",
        "application/vnd.ms-excel"
      ),
      observe: "response",
    };

    return this.http
      .post<any>(url, file, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  createSubscription(body: SubscriptionCreate): Observable<any> {
    const url = this.subscriptionUrl + "";

    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      observe: "response" as "body",
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  updateSubscription(uid: string, body: SubscriptionCreate): Observable<any> {
    const url = this.subscriptionUrl + "/" + uid;

    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return this.http
      .put<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  deleteSubscription(uid: string): Observable<any> {
    const url = this.subscriptionUrl + "/" + uid;

    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return this.http
      .delete<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  getTagsFromSubscription(
    uid: string,
    offset: number,
    limit: number,
    params: any[],
    body: any
  ): Observable<AuthSupportPagedList> {
    const url = `${this.subscriptionUrl}/${uid}/badges/search`;
    let requestString = this.pagination(offset, limit, this.BADGES_LIMIT);
    let queryParams = "";
    params.forEach((p: any) => {
      queryParams += "&" + p.key + "=" + p.value;
    });
    requestString += queryParams;
    const requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      params: requestParams,
    };

    return this.http.post<AuthSupportPagedList>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get tags list from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  getTransactionsFromSubscription(
    uid: string,
    offset: number,
    limit: number,
    params: any[],
    body: any
  ): Observable<TransactionPagedList> {
    const url = `${this.subscriptionUrl}/${uid}/transactions/search`;
    let requestString = this.pagination(offset, limit, this.BADGES_LIMIT);
    let queryParams = "";
    params.forEach((p: any) => {
      queryParams += "&" + p.key + "=" + p.value;
    });
    requestString += queryParams;
    const requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      params: requestParams,
    };

    return this.http.post<TransactionPagedList>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get transaction list from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  exportTagsFromSubscription(uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/badges/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: "blob" as "json",
      observe: "response",
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  exportTransactionsFromSubscription(uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/transactions/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: "blob" as "json",
      observe: "response",
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }
}
